import { CSSProperties, ReactElement, useEffect, useState } from "react";
import { IEAFProduction, IParentSize, IState } from "../../../types";
import {
  FormControl,
  InputLabel,
  MenuItem, Tab, Tabs,
} from "@mui/material";
import { aggregate } from "../../../system/utils";
import Select from "@mui/material/Select";
import TabPanel from "../../../components/tabPanel";
import AdditionsTable from "./kpiAdditions/additionsTable";
import { useSelector } from "react-redux";
import AdditionsChart from "./kpiAdditions/additionsChart";
import { IKPIRecordData } from "./types";
import AdditionsChart2 from "./kpiAdditions/additionsChart2";
import { getPalette } from "../../../configuration/configuration";
import AdditionsChart3 from "./kpiAdditions/additionsChart3";

interface IProps {
  productionData: IEAFProduction[];
  parentSize: IParentSize;
}

// const getElementCsv  = (element?: { weight?: number, suggestedValue?: number, }): string => {
//   const weight = element?.weight ?? 0;
//   const suggestedValue = element?.suggestedValue ?? 0;
//   const diff = weight - suggestedValue;
//
//   return `${suggestedValue.toFixed(2)};${weight.toFixed(2)};${diff.toFixed(2)};`;
// };

const materialList = [
  "DOLO",
  "HICAL"
];

const a11yProps = (index: number): any => {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
};

// const palette = [
//   { suggested: "#6a1b9a", real: "#6a1b9a" },
//   { suggested: "#283593", real: "#283593" },
//   { suggested: "#00838f", real: "#00838f" },
//   { suggested: "#2e7d32", real: "#2e7d32" },
//   { suggested: "#9e9d24", real: "#9e9d24" },
//   { suggested: "#ef6c00", real: "#ef6c00" },
//   { suggested: "#c62828", real: "#c62828" },
// ];

/*
  { suggested: "#6a1b9a", real: "#4527a0" },
  { suggested: "#283593", real: "#1565c0" },
  { suggested: "#00838f", real: "#00695c" },
  { suggested: "#2e7d32", real: "#558b2f" },
  { suggested: "#9e9d24", real: "#f9a825" },
  { suggested: "#ef6c00", real: "#d84315" },
  { suggested: "#c62828", real: "#ad1457" },
 */

const ProductionLimeKPI = ({ productionData, parentSize }: IProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [ groupBy, setGroupBy ] = useState<string>("steel");
  const [tabSelected, setTabSelected] = useState(0);
  const [ groups, setGroups ] = useState<string[]>([]);
  const [ records, setRecords ] = useState<IKPIRecordData[]>([]);
  // const hasScroll = productionData.length * 38 > tableContainerHeight;

  useEffect(() => {
    let index = 8;
    const newData: Record<string, IKPIRecordData> = {};
    const groupList: Set<string> = new Set();

    // newData.DEMO = {
    //   material: "DEMO",
    //   color: getPalette().list[index],
    //   max: 0,
    //   delta: 0,
    //   totalSuggested: 0,
    //   totalReal: 0,
    //   groups: {
    //     "AA": {
    //       weight: aggregate(50),
    //       suggestedValue: aggregate(100),
    //       setPointValue: aggregate(0),
    //       delta: 0
    //     }
    //   }
    // };

    productionData.forEach(row => {
      if(!row.endDatetime) {
        return;
      }

      row.additions.forEach(a => {
        a.additionDetail.forEach(d => {
          const key = d.material.name;

          if (!materialList.includes(key)) {
            return;
          }


          if (!newData[key]) {
            newData[key] = {
              material: key,
              color: getPalette().list[index],
              max: 0,
              delta: 0,
              deltaAbs: 0,
              totalSuggested: 0,
              totalReal: 0,
              groups: {}
            };

            index = (index + 2) % 14;
          }

          let groupValue: string | null = null;

          if (groupBy === "heatNumber") {
            groupValue = row.heatNumber.toString();
          } else if (groupBy === "steel") {
            groupValue = row.steel.code;
          } else if (groupBy === "crew") {
            groupValue = row.crew.name;
          } else if (groupBy === "shift") {
            groupValue = row.shiftHistory.name;
          }

          if (groupValue) {
            groupList.add(groupValue);
            if (!newData[key].groups[groupValue]) {
              newData[key].groups[groupValue] = {
                weight: aggregate(d.weight ?? 0),
                suggestedValue: aggregate(d.suggestedValue ?? 0),
                setPointValue: aggregate(d.setPointValue ?? 0),
                delta: 0,
              };
            } else {
              newData[key].groups[groupValue].weight.add(d.weight ?? 0);
              newData[key].groups[groupValue].suggestedValue.add(d.suggestedValue ?? 0);
              newData[key].groups[groupValue].setPointValue.add(d.setPointValue ?? 0);
            }
          }
        });
      });
    });

    const list = Object.values(newData);

    list.forEach(item => {
      Object.values(item.groups).forEach(gr => {
        const totalWeight = gr.weight.sum();
        const totalSuggested = gr.suggestedValue.sum();
        const totalReal = gr.weight.sum();

        item.max += Math.max(totalWeight, totalSuggested);
        gr.delta = totalWeight - totalSuggested;
        item.totalSuggested += totalSuggested;
        item.totalReal += totalReal;
        item.delta += gr.delta;
        item.deltaAbs += Math.abs(gr.delta);

        // console.log("A->", item.material, item.max, gr.weight.sum(), gr.suggestedValue.sum());
      });
    });

    setGroups([...groupList]);
    setRecords(list);
  }, [productionData, groupBy]);

  const handleChange = (newValue: number) => {
    setTabSelected(newValue);
  };

  const gridStyle: CSSProperties = {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto 1fr",
    justifyItems: "stretch",
    alignItems: "stretch",
    width: parentSize.width
  };


  // const onExportData = () => {
  //   let fileContent = "";
  //   fileContent += ";;;;;;;;;Spar;;;Ladle Graphite;;;Si Mn;;;Fe Si;;;Hi Cal;;;Dolo lime;;;SiC\n";
  //   fileContent += "Heat Number;Steel;Crew;Begin;End;Power On;Power Off;kwh;Weight;SPEC;REAL;DIFF;SPEC;REAL;DIFF;SPEC;REAL;DIFF;SPEC;REAL;DIFF;SPEC;REAL;DIFF;SPEC;REAL;DIFF;SPEC;REAL;DIFF\n";
  //
  //   records.forEach((row: any) => {
  //     fileContent += `${row.heatNumber};${row.steel};${row.crew};${row.beginDatetime};${row.endDatetime};`;
  //     fileContent += `${row.powerOn};${row.powerOff};${row.kwh};${toTons(row.weight).toFixed(2)};`;
  //
  //     fileContent += getElementCsv(row.SPAR);
  //     fileContent += getElementCsv(row.GRAPHITE);
  //     fileContent += getElementCsv(row.SIMN);
  //     fileContent += getElementCsv(row.FESI);
  //     fileContent += getElementCsv(row.HICAL);
  //     fileContent += getElementCsv(row.DOLOLIME);
  //     fileContent += getElementCsv(row.SIC);
  //     fileContent += "\n";
  //   });
  //
  //   const file = new Blob([ fileContent ], { type: "text/csv" });
  //
  //   saveBlobAs(URL.createObjectURL(file), "alloys.csv");
  // };

  return (
    <div className="p-1 display-grid" style={gridStyle}>
      <div className="display-flex-row" style={{ gridRow: "1 / span 1", width: parentSize.width }}>
        <FormControl className="form-control" >
          <InputLabel id="crew-label">Group By</InputLabel>
          <Select
            variant="standard"
            value={groupBy}
            displayEmpty={false}
            style={{ width: 100, margin: 1 }}
            onChange={(event) => setGroupBy(event.target.value as string)}>
            <MenuItem value="heatNumber">Heat Number</MenuItem>
            <MenuItem value="steel">Steel</MenuItem>
            <MenuItem value="crew">Crew</MenuItem>
            <MenuItem value="shift">Shift</MenuItem>
          </Select>
        </FormControl>
      </div>


      <div style={{ gridRow: "2 / span 1", width: parentSize.width }}>
        <Tabs
          value={tabSelected}
          onChange={(_event, value) => handleChange(value)}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab label="Charts A" {...a11yProps(0)} />
          <Tab label="Charts B" {...a11yProps(1)} />
          <Tab label="Charts C" {...a11yProps(2)} disabled={true} />
          <Tab label="Table" {...a11yProps(3)} />
        </Tabs>
      </div>
      <TabPanel value={tabSelected} index={0} style={{ gridRow: "3 / span 1", gridColumn: "1 / 2" }}>
        <AdditionsChart height={parentSize.height - 110} groups={groups} records={records} />
      </TabPanel>
      <TabPanel value={tabSelected} index={1} style={{ gridRow: "3 / span 1", gridColumn: "1 / 2" }}>
        <AdditionsChart2 height={parentSize.height - 110} groups={groups} records={records} />
      </TabPanel>
      <TabPanel value={tabSelected} index={2} style={{ gridRow: "3 / span 1", gridColumn: "1 / 2" }}>
        <AdditionsChart3 height={parentSize.height - 110} groups={groups} records={records} />
      </TabPanel>
      <TabPanel value={tabSelected} index={3} style={{ gridRow: "3 / span 1", gridColumn: "1 / 2" }}>
        <AdditionsTable height={parentSize.height} records={records} groups={groups} />
      </TabPanel>
    </div>
  );
};

export default ProductionLimeKPI;
